import type { TaskListControllerActions } from "./task-list-controller.ts";
import { restoreSubtaskDomOrder } from "./subtask-reorder-controller.ts";

export interface TaskListEventActionCapabilities {
  bindTimeline?(root: ParentNode): void;
  document: Document;
  selectionMode(): boolean;
  selectTask(id: string, selected: boolean): void | Promise<void>;
  openTask(id: string): void | Promise<void>;
  createChildTask?(parentId: string, title: string): void | Promise<void>;
  reorderChildTask?(parentId: string, childId: string, direction: -1 | 1): void | Promise<void>;
  attachChildTask?(parentId: string): void | Promise<void>;
  detachChildTask?(childId: string): void | Promise<void>;
  rememberBoardColumn(collectionId: string, column: string): void;
  captureBoardTask(form: HTMLFormElement, openEditor: boolean): void | Promise<void>;
  closeInlineCapture(): void;
  render(): Promise<unknown>;
  motionBehavior(): ScrollBehavior;
  setTaskDetailsDisclosure(open: boolean): void;
  setComposerDisclosure(group: string, open: boolean): void;
  persistDisclosures(): void | Promise<void>;
  resolveConflicts(id: string): void | Promise<void>;
  loadPreviews(id: string, trigger: HTMLElement): void | Promise<void>;
  createFromTemplate(id: string): void | Promise<void>;
  localUndo(message: string, undo: () => void): void;
  moveTask(id: string, direction: -1 | 1): void | Promise<void>;
  moveBoardTaskToColumn?(id: string, column: string | null): void | Promise<void>;
  shiftTimeline(collectionId: string, windows: number): void | Promise<void>;
  jumpTimeline(collectionId: string, date: string): void | Promise<void>;
  resetTimeline(collectionId: string): void | Promise<void>;
  showMoreTimeline(collectionId: string): void | Promise<void>;
}

export function createTaskListEventActions(capabilities: TaskListEventActionCapabilities): () => TaskListControllerActions {
  function jumpToGroup(id: string): void {
    const group = capabilities.document.getElementById(id);
    group?.scrollIntoView({ behavior: capabilities.motionBehavior(), block: "start" });
    group?.querySelector<HTMLElement>("[data-todo]")?.focus({ preventScroll: true });
  }

  function taskDetailsToggled(open: boolean): void {
    capabilities.setTaskDetailsDisclosure(open);
    void capabilities.persistDisclosures();
  }

  function composerGroupToggled(group: string, open: boolean): void {
    capabilities.setComposerDisclosure(group, open);
    void capabilities.persistDisclosures();
  }

  function removeSubtask(row: HTMLElement, list: HTMLElement, next: ChildNode | null): void {
    capabilities.localUndo("Subtask removed", () => {
      if (!capabilities.document.contains(list)) return;
      list.insertBefore(row, next?.parentNode === list ? next : null);
      row.querySelector<HTMLInputElement>("[name=subtaskTitle]")?.focus();
    });
  }

  function moveSubtask(row: HTMLElement, list: HTMLElement, direction: -1 | 1, originalIds: string[]): void {
    capabilities.localUndo(`Moved subtask ${direction < 0 ? "up" : "down"}`, () => restoreSubtaskDomOrder(list, originalIds));
    row.querySelector<HTMLButtonElement>(`[data-move-subtask="${direction < 0 ? "up" : "down"}"]`)?.focus();
  }

  return () => ({
    bindTimeline: capabilities.bindTimeline,
    selectionMode: capabilities.selectionMode(), selectTask: capabilities.selectTask, openTask: capabilities.openTask,
    rememberBoardColumn: capabilities.rememberBoardColumn, captureBoardTask: capabilities.captureBoardTask,
    cancelBoardCapture: async () => { capabilities.closeInlineCapture(); await capabilities.render(); },
    jumpToGroup, taskDetailsToggled, composerGroupToggled, openDependency: capabilities.openTask, createChildTask: capabilities.createChildTask, reorderChildTask: capabilities.reorderChildTask, attachChildTask: capabilities.attachChildTask, detachChildTask: capabilities.detachChildTask,
    resolveConflicts: capabilities.resolveConflicts, loadPreviews: capabilities.loadPreviews, createFromTemplate: capabilities.createFromTemplate,
    removeSubtask, moveTask: capabilities.moveTask, moveBoardTaskToColumn: capabilities.moveBoardTaskToColumn, moveSubtask,
    shiftTimeline: capabilities.shiftTimeline, jumpTimeline: capabilities.jumpTimeline, resetTimeline: capabilities.resetTimeline, showMoreTimeline: capabilities.showMoreTimeline,
  });
}
